import FalconComponentCard from "components/common/FalconComponentCard";
import Flex from "components/common/Flex";
import useAdvanceTable from "hooks/useAdvanceTable";
import useNotification from "hooks/useNotification";
import { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Dropdown, Row } from "react-bootstrap";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import ArticleSearchResults from "./ArticleSearchResults";
import Skeleton from "react-loading-skeleton";
import EmptyList from "components/common/EmptyList";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFolder } from "@fortawesome/free-regular-svg-icons";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import KbArticlesService from "services/KbArticles.service";
import SubtleBadge from "components/common/SubtleBadge";
import { useFormatDateTime } from "hooks/useFormatDateTime";


const ArticleSearch = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showErrorNotification } = useNotification();
  const formatDateTime = useFormatDateTime();

  // Estados
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Extrair searchTerm da URL
  useEffect(() => {
    const urlSearchTerm = searchParams.get('searchTerm');
    if (urlSearchTerm) {
      setSearchTerm(urlSearchTerm);
    }
  }, [searchParams]);

  // Função para buscar artigos
  const searchArticles = async (term) => {
    if (!term || term.trim() === '') {
      setSearchResults([]);
      return;
    }

    try {
      setIsLoading(true);
      const response = await KbArticlesService.search(term.trim());
      setSearchResults(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Erro ao buscar artigos:', error);
      showErrorNotification('Erro ao buscar artigos');
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Effect para buscar quando o searchTerm muda
  useEffect(() => {
    if (searchTerm) {
      searchArticles(searchTerm);
    }
  }, [searchTerm]);

  // Configuração das colunas da tabela
  const columns = useMemo(
    () => [
      {
        accessorKey: 'title',
        header: 'Título',
        cell: rowData => {
          const { id, title, status } = rowData.row.original;
          const isDraft = status?.status && status?.status === 'DRAFT';
          return (
            <Flex>
              <Link
                to={`/documents/articles/${id}`}
                className="fw-semi-bold fs-9"
                style={{
                  maxWidth: '300px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {title}
              </Link>
              {isDraft && (
                <SubtleBadge className={'ms-2'} bg="warning">
                  Rascunho
                </SubtleBadge>
              )}
            </Flex>
          );
        },
        meta: {
          headerProps: { className: 'pe-1 text-900' },
          cellProps: { className: 'py-2' }
        }
      },
      {
        accessorKey: 'kbFolder',
        header: 'Pasta',
        cell: rowData => {
          const { kbFolder } = rowData.row.original;
          return (
            <Flex alignItems="center">
              <FontAwesomeIcon
                icon={faFolder}
                className="me-2"
                style={{ color: '#0C446E' }}
              />
              <div>
                <div
                  className="text-700 fs-9 fw-semi-bold"
                  style={{
                    maxWidth: '150px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {kbFolder?.name || '---'}
                </div>
                <div
                  className="text-500 fs-10"
                  style={{
                    maxWidth: '150px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {kbFolder?.kbGroup?.name || '---'}
                </div>
              </div>
            </Flex>
          );
        },
        meta: {
          headerProps: { className: 'text-900' },
          cellProps: { className: 'py-2' }
        }
      },
      {
        accessorKey: 'user',
        header: 'Criado por',
        meta: {
          headerProps: { className: 'text-900' }
        },
        cell: rowData => {
          const { user } = rowData.row.original;
          return (
            <span
              className="text-start d-block text-700 fs-9"
              style={{
                maxWidth: '120px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {user ? `${user.firstName} ${user.lastName}` : 'Sistema'}
            </span>
          );
        }
      },
      {
        accessorKey: 'creationDate',
        header: 'Data Criação',
        meta: {
          headerProps: { className: 'text-900' }
        },
        cell: rowData => {
          const { creationDate } = rowData.row.original;
          return (
            <span
              className="text-start text-700 fs-9"
              style={{
                maxWidth: '150px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {creationDate ? formatDateTime(creationDate) : '---'}
            </span>
          );
        }
      },
      {
        accessorKey: 'lastUpdateDate',
        header: 'Data Atualização',
        meta: {
          headerProps: { className: 'text-900' }
        },
        cell: rowData => {
          const { lastUpdateDate } = rowData.row.original;
          return (
            <span
              className="text-start text-700 fs-9"
              style={{
                maxWidth: '150px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {lastUpdateDate ? formatDateTime(lastUpdateDate) : '---'}
            </span>
          );
        }
      }
    ],
    []
  );

  const table = useAdvanceTable({
    data: searchResults,
    columns,
    sortable: true,
    selection: false // Desabilitando seleção para simplificar
  });

  const isEmpty = !isLoading && searchResults.length === 0;
  const hasSearchTerm = searchTerm && searchTerm.trim() !== '';

  return (
    <FalconComponentCard style={{ minHeight: '87vh' }}>
      <Card.Header>
        <Flex
          className={'w-100'}
          justifyContent={'between'}
          alignItems={'center'}
        >
          <div className="w-25">
            <ArticleSearchResults />
          </div>

          <Flex>
            <Button
              size="sm"
              variant="primary"
              style={{ borderRadius: '20px 0px 0px 20px' }}
              onClick={() => navigate('/documents/articles/new')}
            >
              <FontAwesomeIcon icon={faPlus} size="sm" className="me-2" />
              Novo artigo
            </Button>

            <Dropdown>
              <Dropdown.Toggle
                type="button"
                variant="primary"
                size="sm"
                style={{
                  borderRadius: '0px 20px 20px 0px',
                  height: '100%'
                }}
              />
              <Dropdown.Menu style={{ width: '150px' }}>
                <Dropdown.Item
                  as={Button}
                  variant="link"
                  className="text-muted fs-10"
                  type="button"
                  onClick={() => navigate('/documents/articles')}
                >
                  Ver todos os artigos
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Flex>
        </Flex>
      </Card.Header>

      <FalconComponentCard.Body>
        <Row className="h-100">
          <Col md={12} className="h-100">
            <Flex
              justifyContent="between"
              alignItems="center"
              className="mb-3"
            >
              <h5 className="mb-0">
                <strong>
                  {hasSearchTerm
                    ? `Resultados da busca por: "${searchTerm}"`
                    : 'Busca de Artigos'
                  }
                </strong>
              </h5>
              {hasSearchTerm && !isLoading && (
                <span className="text-500 fs-9">
                  {searchResults.length} {searchResults.length === 1 ? 'resultado encontrado' : 'resultados encontrados'}
                </span>
              )}
            </Flex>

            {isLoading ? (
              <Skeleton count={5} height={53} baseColor="#5E6E82" />
            ) : !hasSearchTerm ? (
              <div className="text-center py-5">
                <h6 className="text-500">
                  Use a barra de pesquisa acima para buscar artigos
                </h6>
              </div>
            ) : isEmpty ? (
              <div className="text-center py-5">
                <EmptyList
                  imgSize={300}
                  vhSize={50}
                  title="Nenhum artigo encontrado"
                  subtitle={`Não foram encontrados artigos para "${searchTerm}"`}
                />
              </div>
            ) : (
              <div className="d-flex flex-column h-100">
                <div className="flex-grow-1">
                  <AdvanceTableProvider {...table}>
                    <AdvanceTable
                      bodyClassName="min-height-100"
                      table
                      headerClassName="text-nowrap align-middle"
                      rowClassName="align-middle white-space-nowrap"
                      tableProps={{
                        size: 'sm',
                        striped: true,
                        className: 'fs-9 mb-0 overflow-hidden'
                      }}
                    />
                  </AdvanceTableProvider>
                </div>
              </div>
            )}
          </Col>
        </Row>
      </FalconComponentCard.Body>
    </FalconComponentCard>
  );
};

export default ArticleSearch;